import React, { useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTable, usePagination } from "react-table";
import Nav from "../Components/Nav/Nav";
import Login from "./Login";
import { Link, useNavigate } from "react-router-dom";
import ImageProfil from "../Components/Profil/ImageProfil";
import AdminMenu from "../Components/Admin/AdminMenu";
import AdminFormHeader from "../Components/Admin/AdminFormHeader";
import UpdateProfil from "../Components/Profil/UpdateProfil";
import ProfilDelete from "../Components/Profil/profilDelete";
import { standByUser } from "../actions/user.actions";
import { getUsers } from "../actions/users.actions";
import { Eye, Pause, Play, UserPlus } from "lucide-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdminUsers = () => {
  const userData = useSelector((state) => state.userReducer);
  const usersData = useSelector((state) => state.usersReducer);
  const [query, setQuery] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleStandBy = useCallback(
    (event, userId) => {
      event.preventDefault();
      dispatch(standByUser(userId)).then(() => {
        dispatch(getUsers());
      });
    },
    [dispatch]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Utilisateur",
        accessor: "profileImage",
        Cell: ({ cell }) => {
          const user = cell.row.original;
          return (
            <div className="admin-user-cell">
              <ImageProfil id={user.id} />
              <div className="admin-user-meta">
                <span className="admin-user-name">
                  {user.firstname} {user.name}
                </span>
                <span
                  className={`admin-status ${
                    user.isActif ? "is-active" : "is-paused"
                  }`}
                >
                  <span className="admin-status-dot" />
                  {user.isActif ? "Actif" : "En pause"}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        Header: "Service",
        accessor: "service",
        Cell: ({ value }) => <span className="admin-user-service">{value}</span>,
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ cell }) => {
          const user = cell.row.original;
          return (
            <div className="admin-user-actions">
              <button
                className="ds-btn ds-btn--ghost"
                onClick={() => navigate(`/userprofil/${user.id}`)}
              >
                <Eye size={14} />
                Voir
              </button>
              <UpdateProfil userId={user.id} variant="inline" />
              <button
                className={`ds-btn ${
                  user.isActif ? "ds-btn--ghost" : "ds-btn--primary"
                }`}
                onClick={(event) => handleStandBy(event, user.id)}
              >
                {user.isActif ? <Pause size={14} /> : <Play size={14} />}
                {user.isActif ? "Mettre en pause" : "Réactiver"}
              </button>
              <ProfilDelete
                userId={user.id}
                variant="inline"
                onDeleted={() => dispatch(getUsers())}
              />
            </div>
          );
        },
      },
    ],
    [handleStandBy, dispatch, navigate]
  );

  const data = useMemo(() => {
    if (!Array.isArray(usersData)) return [];
    return usersData.filter(
      (users) =>
        users.firstname.toLowerCase().includes(query) ||
        users.name.toLowerCase().includes(query)
    );
  }, [usersData, query]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    gotoPage,
    setPageSize,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 5 },
    },
    usePagination
  );

  const { pageIndex, pageSize } = state;

  const searchToggle = () => {
    const toggleSearch = document.querySelector(".userlist_admin");
    toggleSearch.classList.toggle("active");
  };

  return (
    <>
      {userData.isAdmin ? (
        <>
          <ToastContainer />
          <Nav />
          <div className="mainContainer">
            <AdminMenu />
            <div className="centerBlock_admin">
              <AdminFormHeader
                title="Gérer les utilisateurs"
                subtitle="Recherchez, activez ou désactivez les comptes de l'équipe."
                actions={
                  <Link to={"/signup"}>
                    <button className="greenButton">
                      <UserPlus size={16} />
                      Créer un utilisateur
                    </button>
                  </Link>
                }
              />
              <div className="search_admin">
                <div className="searchAdmin">
                  <div className="search-icon-container">
                    <i className="fa-solid fa-magnifying-glass search-icon"></i>
                  </div>
                  <input
                    type="text"
                    placeholder=" Rechercher un utilisateur"
                    className="searchbar_admin"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onClick={searchToggle}
                  />
                </div>

                <div className="admin-table-wrap">
                <table
                  {...getTableProps()}
                  className="useronline-container-admin userlist_admin"
                >
                  <thead>
                    {headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th {...column.getHeaderProps()}>
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()} key={i}>
                          {row.cells.map((cell) => {
                            return (
                              <td {...cell.getCellProps()}>
                                {cell.render("Cell")}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                <div className="pagination">
                  <button
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                  >
                    Précédent
                  </button>
                  <button onClick={() => nextPage()} disabled={!canNextPage}>
                    Suivant
                  </button>
                  <span>
                    Page{" "}
                    <strong>
                      {pageIndex + 1} sur {pageOptions.length}
                    </strong>{" "}
                  </span>
                  <span>
                    Aller à la page :{" "}
                    <input
                      type="number"
                      defaultValue={pageIndex + 1}
                      onChange={(e) => {
                        const pageNumber = e.target.value
                          ? Number(e.target.value) - 1
                          : 0;
                        gotoPage(pageNumber);
                      }}
                      style={{ width: "50px" }}
                    />
                  </span>{" "}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    {[5, 10, 20].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        Afficher {pageSize}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <Login />
      )}
    </>
  );
};

export default AdminUsers;
